import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Briefcase,
  Building2,
  Check,
  FlaskConical,
  Loader2,
  Mail,
  Plug,
  Send,
  User,
} from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../../css/Contact.css";
import SEOHead from "../SEO/SEOHead";

import toast, { Toaster } from "react-hot-toast";

import axios from "axios";

/* CAPTCHA */
import Captcha from "./Captcha";
import FrothAiIcon from "../common/FrothAiIcon";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  company: "",
  designation: "",
  message: "",
};

const TOPICS = [
  "A walkthrough of the platform for your team",
  "How FrothAI fits your test design and automation",
  "Connecting Jira, Azure DevOps, GitHub and your other tools",
  "Importing your existing test cases from XLSX or CSV",
];

const EXPLORE = [
  { to: "/froth-ai", icon: FrothAiIcon, title: "FrothAI", text: "Generate requirements, test cases and scripts." },
  { to: "/test-labs", icon: FlaskConical, title: "Test Labs", text: "API, performance and security testing." },
  { to: "/integration", icon: Plug, title: "Integrations", text: "Work with the tools you already use." },
];

function Field({ id, label, icon: Icon, children }) {
  return (
    <div className="ct-field">
      <label htmlFor={id}>
        {label} <span aria-hidden="true">*</span>
      </label>
      <div className="ct-input">
        {Icon && <Icon size={18} className="ct-input__icon" aria-hidden="true" />}
        {children}
      </div>
    </div>
  );
}

function ContactUS() {
  const captchaRef = useRef(null);
  const title = "Contact Froth TestOps | Talk to Our Team";
  const description =
    "Talk to the Froth TestOps team about AI-powered test management, automation and security testing for your business.";

  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isCaptchaValid) {
      toast.error("Please complete captcha");
      return;
    }

    const payload = {
      company: formData.company,
      last_name: formData.name,
      email: formData.email,
      phone: formData.phone,
      designation: formData.designation,
      message: formData.message,
    };

    setSubmitting(true);
    try {
      const response = await axios.post(
        // Per-environment backend, so dev/UAT builds don't create production leads.
        `${process.env.REACT_APP_BASE_URL || "https://frothtestops.com"}/api/create_lead`,
        payload,
      );

      if (response?.data?.status === "success") {
        toast.success("Thank you for contacting us.");
        setFormData(EMPTY_FORM);
        setIsCaptchaValid(false);
        captchaRef.current?.resetCaptcha();
      } else {
        toast.error("Something went wrong.");
      }
    } catch (error) {
      console.error(error);

      if (error?.response?.data?.detail?.length > 0) {
        error.response.data.detail.forEach((err) => {
          toast.error(err.message.replace("Value error, ", ""));
        });
      } else {
        toast.error("Submission failed.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead title={title} description={description} keywords="contact Froth TestOps, test management tools for businesses" />
      <Toaster position="top-right" />

      {/* One screen: a split panel with the brand side on the left and the form on the right. */}
      <section className="ct-page">
        <div className="ft-container">
          <div className="ct-shell">
            {/* INTRO */}
            <aside className="ct-intro">
              <nav className="ft-breadcrumb ct-crumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span aria-hidden="true">/</span>
                <span aria-current="page">Contact</span>
              </nav>
              <span className="ct-eyebrow">
                <span className="ct-eyebrow__dot" aria-hidden="true" />
                Contact us
              </span>
              <h1 className="ct-title">
                Let's talk about your <span>testing workflow</span>
              </h1>
              <p className="ct-lead">
                Tell us a little about your team and what you're testing. We'll get back to you to set up a
                conversation.
              </p>

              <div className="ct-topics">
                <p className="ct-topics__title">What we can help with</p>
                <ul className="ct-topics__list">
                  {TOPICS.map((t) => (
                    <li key={t}>
                      <Check size={14} strokeWidth={3} aria-hidden="true" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>

              <a className="ct-mail" href="mailto:support@frothtestops.com">
                <span className="ct-mail__icon">
                  <Mail size={18} aria-hidden="true" />
                </span>
                <span>
                  <small>Prefer email?</small>
                  support@frothtestops.com
                </span>
                <ArrowUpRight size={18} className="ct-mail__arrow" aria-hidden="true" />
              </a>
            </aside>

            {/* FORM */}
            <div className="ct-card">
              <div className="ct-card__head">
                <h2>Send us a message</h2>
                <p>All fields are required.</p>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="ct-grid">
                  <Field id="ct-name" label="Name" icon={User}>
                    <input
                      id="ct-name"
                      type="text"
                      name="name"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      autoComplete="name"
                      maxLength={150}
                    />
                  </Field>

                  <div className="ct-field">
                    <label htmlFor="ct-phone">
                      Mobile Number <span aria-hidden="true">*</span>
                    </label>
                    <PhoneInput
                      country={"in"}
                      value={formData.phone}
                      onChange={(phone) => setFormData((prev) => ({ ...prev, phone }))}
                      inputProps={{ id: "ct-phone", required: true, autoComplete: "tel" }}
                      containerClass="ct-phone"
                      inputClass="ct-phone__input"
                      buttonClass="ct-phone__flag"
                      placeholder="Mobile Number"
                    />
                  </div>

                  <Field id="ct-email" label="Work Email" icon={Mail}>
                    <input
                      id="ct-email"
                      type="email"
                      name="email"
                      placeholder="you@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </Field>

                  <Field id="ct-company" label="Company" icon={Building2}>
                    <input
                      id="ct-company"
                      type="text"
                      name="company"
                      placeholder="Company name"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      autoComplete="organization"
                    />
                  </Field>

                  <div className="ct-grid__full">
                    <Field id="ct-designation" label="Designation" icon={Briefcase}>
                      <input
                        id="ct-designation"
                        type="text"
                        name="designation"
                        placeholder="e.g. QA Lead"
                        value={formData.designation}
                        onChange={handleChange}
                        required
                        autoComplete="organization-title"
                      />
                    </Field>
                  </div>

                  <div className="ct-field ct-grid__full">
                    <label htmlFor="ct-message">
                      Message <span aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id="ct-message"
                      name="message"
                      placeholder="What would you like to talk about?"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      required
                    />
                  </div>
                </div>

                <Captcha ref={captchaRef} onSolvedChange={setIsCaptchaValid} />

                <button type="submit" className="ft-btn ft-btn--primary ct-submit" disabled={!isCaptchaValid || submitting}>
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="ct-spin" aria-hidden="true" /> Sending…
                    </>
                  ) : (
                    <>
                      Send message <Send size={17} aria-hidden="true" />
                    </>
                  )}
                </button>

                <p className="ct-note">
                  By submitting, you agree to our <Link to="/privacy-policy">Privacy Policy</Link>.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* EXPLORE */}
      <section className="ft-section ft-section--tight ct-explore">
        <div className="ft-container">
          <p className="ct-explore__title">Not ready to talk yet? Explore the platform</p>
          <div className="ft-grid ft-grid--3">
            {EXPLORE.map(({ to, icon: Icon, title: t, text }) => (
              <Link key={to} to={to} className="ct-explore__card">
                <span className="ft-card__icon">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <span className="ct-explore__body">
                  <strong>{t}</strong>
                  <span>{text}</span>
                </span>
                <ArrowRight size={18} className="ct-explore__arrow" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default ContactUS;
